import { useState, useEffect, useRef, ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ProductData,
  PartGroup,
  WizardStep,
  IssueReason,
  YedekParcaData,
  MediaFile,
  ActionType,
  RequestType,
  UsedWarrantyPart
} from '../types/ServiceRequest';
import { getPartGroups, getRequestNumber, createServiceRequest, createServiceRequestSparePart, getServiceRequestDetail, getIssueReasons, getServiceRequestSpareParts, checkUsedWarrantyParts } from '../services/serviceRequestService';
import { checkSerialNumber, getNotesData, getRecordFiles } from '../services/api';
// Modal'lar artık PartSelectionStep içinde import ediliyor
import IssueReasonSelector from '../components/IssueReasonSelector';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SuccessAnimation from '../components/SuccessAnimation';
import PartSelectionStep from '../components/PartSelectionStep';
import MediaUploadStep from '../components/MediaUploadStep';
import { EtkinlikTipi, IslemAdi } from '../constants/status';
import { API_BASE_URL } from '../constants/api';
import Toast, { ToastType } from '../components/Toast';
import BuyerInfoModal from '../components/BuyerInfoModal';
import BarcodeScanner from '../components/BarcodeScanner';
import { FileService } from '../services/fileService';
import { v4 as uuidv4 } from 'uuid';
import PageTransition from '../components/PageTransition';
import { useUser } from '../contexts/UserContext';
import { useMenu } from '../contexts/MenuContext';
import { formatPhoneNumber, isValidTurkishMobile } from '../utils/phoneUtils';
import { notifyAccountCacheUpdate } from '../utils/accountCacheUtils';

// Seçilen yedek parça türü tanımı - artık sadece seçilen parçalar tutulacak
interface SelectedSparePart {
  part: YedekParcaData;
  quantity: number;
}

// MediaFile interface'ine upload durumu ve mevcut dosya bilgileri eklendi
interface MediaFileWithStatus extends MediaFile {
  uploadStatus?: 'uploading' | 'success' | 'error';
  existingFileId?: number;
  existingFileRumuz?: string;
  streamUrl?: string; // Video stream için ayrı URL
}

export default function ServiceRequest() {
  const navigate = useNavigate();
  const { id: reviseId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { userData } = useUser();
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  
  // Scroll yönünü takip etmek için state ve ref
  const [showBackButton, setShowBackButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRequestCompleted, setIsRequestCompleted] = useState(false); // Talep tamamlandığında kalıcı olarak gizle
  const lastScrollTop = useRef(0);
  
  // Geri gitme fonksiyonu - bir önceki adıma dön
  const handleGoBack = () => {
    switch (currentStep) {
      case WizardStep.PRODUCT_DETAIL:
        setDirection(-1);
        setCurrentStep(WizardStep.SERIAL_CHECK);
        updateUrlParams(WizardStep.SERIAL_CHECK);
        break;
      case WizardStep.ISSUE_DETAILS:
        setDirection(-1);
        setCurrentStep(WizardStep.PRODUCT_DETAIL);
        updateUrlParams(WizardStep.PRODUCT_DETAIL);
        break;
      case WizardStep.PART_SELECTION:
        setDirection(-1);
        setCurrentStep(WizardStep.ISSUE_DETAILS);
        updateUrlParams(WizardStep.ISSUE_DETAILS);
        break;
      case WizardStep.MEDIA_UPLOAD:
        setDirection(-1);
        setCurrentStep(WizardStep.PART_SELECTION);
        updateUrlParams(WizardStep.PART_SELECTION);
        break;
      default:
        // İlk adımdaysa tarayıcı geçmişine dön
        navigate(-1);
        break;
    }
  };
  
  // Revize modunda olup olmadığını kontrol et
  const isReviseMode = Boolean(reviseId);
  
  // Sayfanın en üstüne scroll fonksiyonu
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // URL parametrelerini güncelleyen yardımcı fonksiyon
  const updateUrlParams = (step: WizardStep, replaceHistory: boolean = false) => {
    const newParams = new URLSearchParams(window.location.search);
    
    // Adım parametresini güncelle
    if (step === WizardStep.SERIAL_CHECK) {
      newParams.delete('step');
    } else {
      newParams.set('step', step.toString());
    }
    
    navigate({ search: newParams.toString() }, { replace: replaceHistory });
  };

  // Adım geçişi validasyon fonksiyonu
  const isValidStepTransition = (fromStep: WizardStep, toStep: WizardStep): boolean => {
    // İlk adıma her zaman dönülebilir
    if (toStep === WizardStep.SERIAL_CHECK) {
      return true;
    }

    // Revize modunda değilse ve ürün verisi yoksa sadece ilk adıma gidilebilir
    if (!isReviseMode && !productData) {
      return false;
    }

    // Ürün detayına geçmek için ürün verisi gerekli
    if (toStep === WizardStep.PRODUCT_DETAIL && !productData) {
      return false;
    }

    // Arıza detaylarına geçmek için ürün verisi ve garanti/onay durumu gerekli
    if (toStep === WizardStep.ISSUE_DETAILS) {
      if (!productData) return false;
      
      const isWarrantyValid = productData.GarantiDurumu === 1;
      const isApprovalValid = productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI;
      const canProceedInReviseMode = isReviseMode ? (isWarrantyValid && isApprovalValid) : isWarrantyValid;
      
      return canProceedInReviseMode;
    }

    // Parça seçimi ve medya yükleme için arıza detayları gerekli
    if (toStep === WizardStep.PART_SELECTION || toStep === WizardStep.MEDIA_UPLOAD) {
      if (!productData || !issueReason) return false;
      
      const isWarrantyValid = productData.GarantiDurumu === 1;
      const isApprovalValid = productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI;
      const canProceedInReviseMode = isReviseMode ? (isWarrantyValid && isApprovalValid) : isWarrantyValid;
      const isMileageValid = userData?.KmIsRequired ? !!mileage.trim() : true;
      const isServiceNotesValid = !!serviceNotes.trim();
      
      return canProceedInReviseMode && isMileageValid && isServiceNotesValid;
    }

    return true;
  };
  const [currentStep, setCurrentStep] = useState<WizardStep>(() => {
    const stepParam = searchParams.get('step');
    if (stepParam && Object.values(WizardStep).includes(parseInt(stepParam) as WizardStep)) {
      return parseInt(stepParam) as WizardStep;
    }
    return WizardStep.SERIAL_CHECK;
  });
  const [serialNumber, setSerialNumber] = useState('');
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState(0); // 1 = ileri, -1 = geri
  const [isInitialRender, setIsInitialRender] = useState(true);

  // Yeni ekran için state değişkenleri
  const [mileage, setMileage] = useState('');
  const [issueReason, setIssueReason] = useState<IssueReason | null>(null);
  const [mileageError, setMileageError] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [serviceNotesError, setServiceNotesError] = useState('');

  // Arıza nedenleri için state
  const [issueReasons, setIssueReasons] = useState<IssueReason[]>([]);
  const [loadingIssueReasons, setLoadingIssueReasons] = useState(false);
  const [issueReasonModalOpen, setIssueReasonModalOpen] = useState(false);
  const [issueReasonSearchTerm, setIssueReasonSearchTerm] = useState('');

  // Parça grubu ve parça seçimi için state değişkenleri
  const [partGroups, setPartGroups] = useState<PartGroup[]>([]);
  const [loadingPartGroups, setLoadingPartGroups] = useState(false);
  const [partGroupError, setPartGroupError] = useState('');
  const [partNameError, setPartNameError] = useState('');

  // Dropdown için state ve ref'ler
  const [searchTerm, setSearchTerm] = useState('');

  // Seçilen yedek parçalar için state
  const [selectedSpareParts, setSelectedSpareParts] = useState<SelectedSparePart[]>([]);

  // Yedek parçalar için loading state (sadece büyüteç için)
  const [loadingYedekParcalar, setLoadingYedekParcalar] = useState(false);

  // Medya dosyaları için state değişkenleri
  const [mediaFiles, setMediaFiles] = useState<MediaFileWithStatus[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState('');
  // File input ref'leri artık MediaUploadStep component'inde - Eski kodlar için temp olarak korunuyor
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<number | null>(null);
  const skipWarrantyPartCheckRef = useRef(false);
  const [warrantyPartCheckPending, setWarrantyPartCheckPending] = useState(false);
  const [usedPartsDialog, setUsedPartsDialog] = useState<{ isOpen: boolean; parts: UsedWarrantyPart[] }>({
    isOpen: false,
    parts: []
  });
  
  // Alıcı bilgileri modal state
  const [isBuyerInfoModalOpen, setIsBuyerInfoModalOpen] = useState(false);
  
  // Barkod okuyucu modal state
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);


  
  // Toast ile ilgili state değişkenleri
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info' as ToastType
  });
  
  // Revize modu için state
  const [reviseData, setReviseData] = useState<any>(null);
  const [isLoadingReviseData, setIsLoadingReviseData] = useState(false);
  
  // Adımlar arası geçişte şartlı olarak scroll yapma
  const [skipScroll, setSkipScroll] = useState(false);
  
  // URL parametrelerini dinle ve adımı senkronize et
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const urlStep = stepParam ? parseInt(stepParam) as WizardStep : WizardStep.SERIAL_CHECK;
    
    // Sayfa yenilendiğinde veya ilk yüklemede ürün verisi yoksa ilk adıma dön
    if (!isReviseMode && !productData && urlStep !== WizardStep.SERIAL_CHECK) {
      setCurrentStep(WizardStep.SERIAL_CHECK);
      updateUrlParams(WizardStep.SERIAL_CHECK, true);
      return;
    }
    
    if (Object.values(WizardStep).includes(urlStep) && urlStep !== currentStep) {
      // Yön belirleme
      if (urlStep > currentStep) {
        setDirection(1);
      } else if (urlStep < currentStep) {
        setDirection(-1);
      }
      
      // Adım geçişi validasyonu
      if (isValidStepTransition(currentStep, urlStep)) {
        setCurrentStep(urlStep);
      } else {
        // Geçersiz geçiş ise ilk adıma dön
        setCurrentStep(WizardStep.SERIAL_CHECK);
        updateUrlParams(WizardStep.SERIAL_CHECK, true);
      }
    }
  }, [searchParams, productData, isReviseMode]);
  
  useEffect(() => {
    // skipScroll true ise scroll yapma ve false'a geri döndür
    if (skipScroll) {
      setSkipScroll(false);
      return;
    }
    
    // Aksi durumda normal scroll yap
    scrollToTop();
  }, [currentStep, skipScroll]);
  
  // Tarayıcı geri tuşunu engelleme (dosya yüklemesi sırasında)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploadingFiles || isUploading) {
        e.preventDefault();
        e.returnValue = 'Dosya yükleme işlemi devam ediyor. Sayfadan ayrılmak istediğinizden emin misiniz?';
        return e.returnValue;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (uploadingFiles || isUploading) {
        e.preventDefault();
        // Kullanıcıyı mevcut sayfada tut
        window.history.pushState(null, '', window.location.href);
        showToast('Dosya yükleme işlemi devam ediyor. Lütfen bekleyin.', 'warning');
      }
    };

    // Event listener'ları ekle
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Yükleme başladığında history state'i ayarla
    if (uploadingFiles || isUploading) {
      window.history.pushState(null, '', window.location.href);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [uploadingFiles, isUploading]);
  
  // Revize modu için veri çekme
  useEffect(() => {
    const fetchReviseData = async () => {
      if (!isReviseMode || !reviseId) return;
      
      try {
        setIsLoadingReviseData(true);
        setLoading(true); // Seri numarası adımında "Kontrol Ediliyor..." göster
        
        const response = await getServiceRequestDetail(reviseId);
        
        if (response.data && response.data.length > 0) {
          const serviceData = response.data[0];
          setReviseData(serviceData);
          
          // Revize verisiyle sihirbaz adımlarını doldur
          await populateWizardSteps(serviceData);
          
          showToast('Talep bilgileri yüklendi. Revize işlemini tamamlayabilirsiniz.', 'success');
        }
      } catch (error) {
        console.error('Revize verileri yüklenirken hata:', error);
        showToast('Talep bilgileri yüklenirken bir hata oluştu.', 'error');
        navigate('/service-requests'); // Hata durumunda geri dön
      } finally {
        setIsLoadingReviseData(false);
        setLoading(false); // Loading durumunu kapat
      }
    };

    fetchReviseData();
  }, [isReviseMode, reviseId, navigate]);
  
  // Toast gösterme fonksiyonu
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };
  
  // Toast kapatma fonksiyonu
  const hideToast = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // Barkod okuyucu handler'ları
  const handleBarcodeScanned = (result: string) => {
    setSerialNumber(result);
    setIsBarcodeScannerOpen(false);
    // Seri numarası değiştiğinde hata mesajını temizle
    if (error) {
      setError('');
    }
    showToast('Barkod başarıyla okundu!', 'success');
  };

  const handleBarcodeScanError = (error: string) => {
    showToast(`Barkod okuma hatası: ${error}`, 'error');
  };

  // Otomatik kontrol fonksiyonu
  const handleAutoCheck = () => {
    // Form submit event'ini simüle et
    const form = document.querySelector('form');
    if (form) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
    }
  };

  // Arıza nedenlerini yükle
  useEffect(() => {
    const fetchIssueReasons = async () => {
      if (currentStep !== WizardStep.ISSUE_DETAILS) return;

      try {
        setLoadingIssueReasons(true);
        const response = await getIssueReasons();

        if (response.data) {
          setIssueReasons(response.data);
        }
      } catch (error) {
        console.error('Arıza nedenleri yüklenirken hata:', error);
        showToast('Arıza nedenleri yüklenirken bir hata oluştu.', 'error');
      } finally {
        setLoadingIssueReasons(false);
      }
    };

    fetchIssueReasons();
  }, [currentStep]);

  // Revize verisiyle sihirbaz adımlarını doldur
  const populateWizardSteps = async (serviceData: any) => {
    try {
      // Seri numarası ve ürün verilerini doldur
      if (serviceData.Kupon && serviceData.Sasi) {
        setSerialNumber(serviceData.Sasi || '');
        
        // ServiceRequestDetail'den gelen temel veriler, garanti bilgileri için checkSerialNumber gerekli
        const productDataFromService: ProductData = {
          Id: serviceData.Id || 0,
          CariAdi: serviceData.CariAdi || '',
          KategoriAdi: serviceData.KategoriAdi || '',
          MarkaAdi: serviceData.MarkaAdi || '',
          Model: serviceData.Model || 0,
          ModelAdi: serviceData.ModelAdi || '',
          RenkAdi: serviceData.RenkAdi || '',
          Sasi: serviceData.Sasi || '',
          Motor: serviceData.Motor || '',
          MotorMu: serviceData.MotorMu || false,
          SaticiTelefon: serviceData.SaticiTelefon || '',
          SaticiAdres: serviceData.SaticiAdres || '',
          Ulke: serviceData.Ulke || undefined,
          SaticiAdi: serviceData.SaticiAdi || undefined,          
          SaticiIl: serviceData.SaticiIl || undefined,
          SaticiIlAdi: serviceData.SaticiIlAdi || '',
          SaticiIlce: serviceData.SaticiIlce || undefined,
          SaticiIlceAdi: serviceData.SaticiIlceAdi || '',
          AliciTelefon: serviceData.AliciTelefon || '',
          AliciTeslimEdilen: serviceData.AliciAdi || '',
          AliciFaturaNo: serviceData.FaturaNo || '',
          AliciFaturaTarihi: '',
          GarantiDurumu: 1, // Varsayılan değer, gerçek bilgi checkSerialNumber'dan gelir
          IslemAdi: IslemAdi.ONAYLANDI, // Revize modunda kupon onaylı olarak ayarla
          SonIslem: ActionType.CREATED, // Varsayılan değer, gerçek bilgi checkSerialNumber'dan gelir (kupon verisi)
          SonEtkinlikTipi: serviceData.SonEtkinlikTipi || EtkinlikTipi.DIGER,
          MontajBedeli: 0 // Varsayılan değer
        };
        
        // Önce garanti bilgilerini al, sonra productData'yı set et
        let finalProductData = productDataFromService;
        
        if (serviceData.Sasi) {
          try {
            const serialCheckResponse = await checkSerialNumber(serviceData.Sasi);
            if (serialCheckResponse.success && serialCheckResponse.data) {
              const warrantyData = serialCheckResponse.data;
              // Garanti bilgileriyle productData'yı güncelle
              finalProductData = {
                ...productDataFromService,
                GarantiDurumu: warrantyData.GarantiDurumu || productDataFromService.GarantiDurumu,
                GarantiDurumuAciklamasi: warrantyData.GarantiDurumuAciklamasi,
                GarantiDevamEdiyorMu: warrantyData.GarantiDevamEdiyorMu,
                GarantiBitisTarihi: warrantyData.GarantiBitisTarihi,
                SonIslem: warrantyData.SonIslem || productDataFromService.SonIslem, // Kupon verisinden SonIslem al
                Ulke: warrantyData.Ulke ?? productDataFromService.Ulke,
                SaticiIl: warrantyData.SaticiIl ?? productDataFromService.SaticiIl,
                SaticiIlAdi: warrantyData.SaticiIlAdi || productDataFromService.SaticiIlAdi,
                SaticiIlce: warrantyData.SaticiIlce ?? productDataFromService.SaticiIlce,
                SaticiIlceAdi: warrantyData.SaticiIlceAdi || productDataFromService.SaticiIlceAdi,
                AliciAdres: warrantyData.AliciAdres,
                AliciEPosta: warrantyData.AliciEPosta,
                AliciBelgeNo: warrantyData.AliciBelgeNo,
                AliciTeslimYeri: warrantyData.AliciTeslimYeri,
                AliciTeslimTarihi: warrantyData.AliciTeslimTarihi,
                AliciFaturaTarihi: warrantyData.AliciFaturaTarihi,
                MontajBedeli: warrantyData.MontajBedeli || productDataFromService.MontajBedeli
              };
            }
          } catch (error) {
            console.error('Garanti bilgileri alınırken hata:', error);
            // Hata durumunda temel verilerle devam et
          }
        }
        
        setProductData(finalProductData);
        
        // Revize modunda kısa bir gecikme sonrası Ürün Bilgileri adımına otomatik geç
        setTimeout(() => {
          setCurrentStep(WizardStep.PRODUCT_DETAIL);
          updateUrlParams(WizardStep.PRODUCT_DETAIL, true);
          
          // Garanti kapsamı dışı veya onay bekliyor durumunda kullanıcıyı bilgilendir
          const isWarrantyValid = finalProductData.GarantiDurumu === 1;
          const isApprovalValid = (finalProductData.IslemAdi === IslemAdi.OLUSTURULDU || finalProductData.IslemAdi === IslemAdi.ONAYLANDI);
          
          if (!isWarrantyValid) {
            showToast('Bu kupon garanti kapsamı dışındadır. Diğer adımlar pasif durumda.', 'warning');
          } else if (!isApprovalValid) {
            showToast('Bu kupon onay bekliyor durumundadır. Diğer adımlar pasif durumda.', 'warning');
          }
        }, 300); // 300ms gecikme ile daha hızlı geçiş
      }
      
      // Kilometre bilgisini doldur
      if (serviceData.Km && userData?.KmIsRequired) {
        setMileage(serviceData.Km.toString());
      }
      
      // Arıza nedeni bilgisini doldur - ArizaNedeniAdi alanından
      if (serviceData.ArizaNedeni && serviceData.ArizaNedeniAdi) {
        // ArizaNedeniAdi alanından dinamik IssueReason objesi oluştur
        const issueReasonFromData: IssueReason = {
          Id: serviceData.ArizaNedeni,
          Ad: serviceData.ArizaNedeniAdi
        };
        setIssueReason(issueReasonFromData);
      }
      
      // Servis notlarını yükle
      if (serviceData.Id) {
        try {
          const notesResponse = await getNotesData('ServisNotlari', 'Talepler', serviceData.Id.toString());
          if (notesResponse.success && notesResponse.data && notesResponse.data.length > 0) {
            // İlk notu al ve Servis Notları alanına yerleştir
            const firstNote = notesResponse.data[0];
            if (firstNote.Notlar) {
              // HTML taglarını kaldırarak sadece metin içeriğini al
              const textContent = firstNote.Notlar.replace(/<[^>]*>/g, '').trim();
              setServiceNotes(textContent);
            }
          }
        } catch (notesError) {
          console.error('Servis notları yüklenirken hata:', notesError);
          // Servis notları yüklenememesi revize işlemini engellemez
        }
      }
      
      // Revize modunda mevcut yedek parçaları yükle
      if (serviceData.Id) {
        try {
          const sparePartsResponse = await getServiceRequestSpareParts(serviceData.Id.toString());
          if (sparePartsResponse.data && sparePartsResponse.data.length > 0) {
            // TalepYedekParcalari verilerini selectedSpareParts formatına çevir
            const formattedSpareParts = sparePartsResponse.data.map(sparePartData => ({
              part: {
                Id: sparePartData.YedekParca,
                Kod: sparePartData.Kod,
                Ad: sparePartData.Ad,
                Gorsel: sparePartData.Gorsel,
                GrupAdi: sparePartData.GrupAdi,
                Stok: sparePartData.Stok || 0,
                CariStokDurumu: sparePartData.CariStokDurumu || false,
                ListeFiyati: sparePartData.ListeFiyati || 0,
                BayiFiyati: sparePartData.BayiFiyati || 0,
                IscilikBedeli: sparePartData.IscilikBedeli || 0,
                PromosyonluMu: sparePartData.PromosyonluMu || false,
                AktifMi: sparePartData.AktifMi !== undefined ? sparePartData.AktifMi : true
              } as YedekParcaData,
              quantity: sparePartData.Miktar
            }));

            setSelectedSpareParts(formattedSpareParts);
          } else {
            setSelectedSpareParts([]);
          }
        } catch (sparePartsError) {
          console.error('Revize modunda yedek parçalar yüklenirken hata:', sparePartsError);
          setSelectedSpareParts([]);
          // Yedek parça yüklenememesi revize işlemini engellemez
        }
      } else {
        setSelectedSpareParts([]);
      }
      
      // Parça gruplarını yükle (parça seçimi adımı için gerekli)
      if (serviceData.KategoriAdi) {
        try {
          const partGroupsData = await getPartGroups(serviceData.KategoriAdi);
          if (partGroupsData.total > 0) {
            setPartGroups(partGroupsData.data);
            
            // Grup verileri yüklendi, parça seçimi için hazır
          }
        } catch (partGroupsError) {
          console.error('Parça grupları yüklenirken hata:', partGroupsError);
          // Parça grupları yüklenememesi revize işlemini engellemez
        }
      }
      
      // Mevcut görselleri yükle (Görsel ve Video Ekle adımı için)
      if (serviceData.Id) {
        try {
          const filesResponse = await getRecordFiles(serviceData.Id.toString(), 'Talepler');
          if (filesResponse.success && filesResponse.data && filesResponse.data.length > 0) {
            // Mevcut dosyaları MediaFileWithStatus formatına çevir
            const existingMediaFiles: MediaFileWithStatus[] = await Promise.all(
              filesResponse.data.map(async (file: any) => {
                try {
                  // Dosya URL'lerini oluştur
                  const posterUrl = `${API_BASE_URL}/files/read/${file.Rumuz}`; // Poster/thumbnail için
                  const streamUrl = `${API_BASE_URL}/files/stream/${file.Rumuz}`; // Video stream için
                  
                  // Dosya tipini belirle (uzantıya göre)
                  const isVideo = file.Tip && (
                    file.Tip.toLowerCase().includes('video') || 
                    file.Ad.toLowerCase().endsWith('.mp4') ||
                    file.Ad.toLowerCase().endsWith('.avi') ||
                    file.Ad.toLowerCase().endsWith('.mov') ||
                    file.Ad.toLowerCase().endsWith('.wmv')
                  );
                  
                  // Fake File object oluştur (revize modunda gerçek file object'i yok)
                  const fakeFile = new File([''], file.Ad, { 
                    type: isVideo ? 'video/mp4' : 'image/jpeg' 
                  });
                  
                  return {
                    id: `existing-${file.Id}`,
                    file: fakeFile,
                    preview: posterUrl, // Poster için /read endpoint
                    streamUrl: isVideo ? streamUrl : undefined, // Video stream için /stream endpoint
                    type: isVideo ? 'video' : 'image',
                    uploading: false,
                    existingFileId: file.Id,
                    existingFileRumuz: file.Rumuz
                  } as MediaFileWithStatus;
                } catch (fileError) {
                  console.error('Dosya işlenirken hata:', fileError);
                  return null;
                }
              })
            );
            
            // Null olmayan dosyaları filtrele ve mediaFiles'a ekle
            const validMediaFiles = existingMediaFiles.filter(file => file !== null);
            setMediaFiles(validMediaFiles);
          }
        } catch (filesError) {
          console.error('Mevcut görseller yüklenirken hata:', filesError);
          // Görsel yüklenememesi revize işlemini engellemez
        }
      }
      
    } catch (error) {
      console.error('Sihirbaz adımları doldurulurken hata:', error);
      showToast('Veriler yüklenirken bir sorun oluştu.', 'error');
    }
  };

  // Alıcı bilgileri modal handler'ları
  const handleBuyerInfoSuccess = (buyerInfo: any) => {
    showToast('Müşteri bilgileri başarıyla kaydedildi.', 'success');
    setIsBuyerInfoModalOpen(false);
    
    // ProductData'yı forma girilen bilgilerle güncelle
    if (productData) {
      setProductData({
        ...productData,
        AliciFaturaNo: buyerInfo.faturaNo || '',
        AliciTeslimEdilen: buyerInfo.teslimEdilen || '',
        AliciTelefon: buyerInfo.telefon || '',
        AliciAdres: buyerInfo.adres || '',
        AliciEPosta: buyerInfo.email || '',
        AliciBelgeNo: buyerInfo.belgeNo || '',
        AliciTeslimYeri: buyerInfo.teslimYeri || '',
        AliciFaturaTarihi: formatDateToApi(buyerInfo.faturaTarihi) || '',
        AliciTeslimTarihi: formatDateToApi(buyerInfo.teslimTarihi) || ''
      });
    }
  };

  const handleBuyerInfoError = (error: string) => {
    showToast(error, 'error');
  };

  const handleBuyerInfoAlreadyExists = (updatedBuyerInfo: any, sonIslem: number) => {
    showToast('Bu ürün için müşteri bilgileri zaten girilmiş.', 'warning');
    setIsBuyerInfoModalOpen(false);
    
    // ProductData'yı API'den çekilen verilerle güncelle
    if (productData) {
      setProductData({
        ...productData,
        AliciFaturaNo: updatedBuyerInfo.faturaNo || '',
        AliciTeslimEdilen: updatedBuyerInfo.teslimEdilen || '',
        AliciTelefon: updatedBuyerInfo.telefon || '',
        AliciAdres: updatedBuyerInfo.adres || '',
        AliciEPosta: updatedBuyerInfo.email || '',
        AliciBelgeNo: updatedBuyerInfo.belgeNo || '',
        AliciTeslimYeri: updatedBuyerInfo.teslimYeri || '',
        AliciFaturaTarihi: formatDateToApi(updatedBuyerInfo.faturaTarihi) || '',
        AliciTeslimTarihi: formatDateToApi(updatedBuyerInfo.teslimTarihi) || '',
        SonIslem: sonIslem // SonIslem değerini güncelle ki buton gizlensin
      });
    }
  };

  // İlk yükleme tamamlandığında isInitialRender'ı false yap
  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    if (!loading && productData) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500); // 500ms gecikme
      
      return () => clearTimeout(timer);
    }
  }, [loading, productData]);

  // Scroll olayını dinle - butonu gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonu gizle
      if (isModalOpen) {
        setShowBackButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonun durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowBackButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Buton için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowBackButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowBackButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonu tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowBackButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonu gizle
        setShowBackButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const triggerRef = useRef<HTMLDivElement>(null);

  // AliciFaturaTarihi tarih formatına dönüştürme
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    // /Date(1738875600000)/ formatını çıkarıp timestamp elde ediyoruz
    const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
    if (isNaN(timestamp)) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR');
  };

  // API'den gelen tarihi modal formuna uygun formata çevirme
  const formatDateFromApi = (dateString: string | undefined) => {
    if (!dateString) return '';
    const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
    if (isNaN(timestamp)) return '';
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  // Form'dan gelen tarihi API formatına çevirme
  const formatDateToApi = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const timestamp = date.getTime();
    return `/Date(${timestamp})/`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await checkSerialNumber(serialNumber);

      if (!result.success || !result.data) {
        setError(result.error || 'Bu seri numarasına sahip bir ürün bulunamadı.');
      } else {
        // Seri numarası değiştiğinde tüm formları sıfırla
        setProductData(result.data);
        setDirection(1); // İleri yönde geçiş
        setCurrentStep(WizardStep.PRODUCT_DETAIL);
        updateUrlParams(WizardStep.PRODUCT_DETAIL);

        // Sihirbazın diğer adımları için verileri sıfırla
        setMileage('');
        setIssueReason(null);
        setMileageError('');
        setReasonError('');
        setServiceNotesError('');
        setSelectedSpareParts([]);
        setServiceNotes('');
        setMediaFiles([]);
      }
    } catch (err) {
      setError('Seri numarası kontrol edilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setDirection(-1); // Geri yönde geçiş
    setCurrentStep(WizardStep.SERIAL_CHECK);
    updateUrlParams(WizardStep.SERIAL_CHECK);
  };

  // Detaylardan sonraki adıma geçiş için işlev
  const handleContinueToIssueDetails = () => {
    setDirection(1);
    setCurrentStep(WizardStep.ISSUE_DETAILS);
    updateUrlParams(WizardStep.ISSUE_DETAILS);
  };

  // Arıza nedeni adımından önceki adıma dönüş
  const handleBackToProductDetails = () => {
    setDirection(-1);
    setCurrentStep(WizardStep.PRODUCT_DETAIL);
    updateUrlParams(WizardStep.PRODUCT_DETAIL);
  };

  // Kilometre değiştiğinde hatayı sıfırla
  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMileage(value);
    if (value.trim()) {
      setMileageError('');
    }
  };

  // Arıza nedeni seçildiğinde hatayı sıfırla
  const handleIssueReasonSelect = (reason: IssueReason) => {
    setIssueReason(reason);
    setReasonError('');
    setIssueReasonModalOpen(false);
    setIssueReasonSearchTerm('');
  };

  // Arıza nedeni arama handler'ı
  const handleIssueReasonSearchChange = (value: string) => {
    setIssueReasonSearchTerm(value);
  };

  // Servis notları değiştiğinde state'i güncelle
  const handleServiceNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setServiceNotes(e.target.value);
    if (e.target.value.trim()) {
      setServiceNotesError('');
    }
  };

  // Arıza detaylarını kontrol eden ve geçerli olduğunda işleyen fonksiyon
  const handleIssueDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMileageError('');
    setReasonError('');
    setServiceNotesError('');

    let isValid = true;

    // Kilometre kontrolü - sadece KmIsRequired true ise zorunlu
    if (userData?.KmIsRequired) {
      if (!mileage.trim()) {
        setMileageError('Kilometre bilgisi zorunludur');
        isValid = false;
      } else if (isNaN(Number(mileage)) || Number(mileage) < 0) {
        setMileageError('Geçerli bir kilometre değeri girin');
        isValid = false;
      }
    }

    // Arıza nedeni kontrolü
    if (!issueReason) {
      setReasonError('Arıza nedeni seçilmelidir');
      isValid = false;
    }
    
    // Servis notları kontrolü
    if (!serviceNotes.trim()) {
      setServiceNotesError('Servis notları zorunludur');
      isValid = false;
    }

    if (!isValid) {
      showToast('Lütfen tüm zorunlu alanları doldurun.', 'error');
      return;
    }

    // Arıza bilgileri doğruysa, parça seçimi adımına geç
    setDirection(1);
    fetchPartGroups();
    setCurrentStep(WizardStep.PART_SELECTION);
    updateUrlParams(WizardStep.PART_SELECTION);
  };

  // Bu fonksiyon artık gerekmiyor - arıza nedenleri dinamik olarak backend'ten geliyor

  // Garanti durumu text'ini döndür
  const getWarrantyStatusText = (status: number | undefined): string => {
    return status === 1 ? 'Garantili' : 'Garanti Dışı';
  };

  // Nokta göstergelerine tıklama işlevi
  const handleStepClick = (step: WizardStep) => {
    // Geçiş yönünü belirle
    if (step < currentStep) {
      setDirection(-1);
    } else if (step > currentStep) {
      setDirection(1);
    } else {
      return; // Aynı adımsa hiçbir şey yapma
    }

    // Geçişleri kontrol et
    if (step === WizardStep.PRODUCT_DETAIL && !productData) {
      return; // Ürün verisi olmadan ürün detayına geçilemez
    }

    // Revize modunda garanti durumu ve onay durumu kontrolleri
    const isWarrantyValid = productData && productData.GarantiDurumu === 1;
    const isApprovalValid = productData && (productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI);
    const canProceedInReviseMode = isReviseMode ? (isWarrantyValid && isApprovalValid) : isWarrantyValid;

    if (step === WizardStep.ISSUE_DETAILS && (!productData || !canProceedInReviseMode)) {
      return; // Ürün verisi olmadan veya garanti/onay durumu uygun değilse arıza detayına geçilemez
    }

    const isIssueDetailsValid = productData && issueReason !== null && 
      (userData?.KmIsRequired ? !!mileage.trim() : true) && !!serviceNotes.trim();

    if ((step === WizardStep.PART_SELECTION || step === WizardStep.MEDIA_UPLOAD) && (!isIssueDetailsValid || !canProceedInReviseMode)) {
      return; // Arıza detayları eksikse veya garanti/onay durumu uygun değilse sonraki adımlara geçilemez
    }

    setCurrentStep(step);
    updateUrlParams(step);
  };

  const renderStepIndicators = () => {
    const isIssueDetailsValid = productData && issueReason !== null && 
      (userData?.KmIsRequired ? !!mileage.trim() : true) && !!serviceNotes.trim();
    const isProcessing = uploadingFiles || isUploading;
    
    // Revize modunda da garanti durumu ve onay durumu kontrol edilmeli
    const isWarrantyValid = productData && productData.GarantiDurumu === 1;
    const isApprovalValid = productData && (productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI);
    
    // Revize modunda hem garanti hem onay durumu geçerli olmalı
    const canProceedInReviseMode = isReviseMode ? (isWarrantyValid && isApprovalValid) : isWarrantyValid;

    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        <button
          onClick={() => handleStepClick(WizardStep.SERIAL_CHECK)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentStep === WizardStep.SERIAL_CHECK
              ? 'bg-blue-600 transform scale-125'
              : isProcessing
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
                : 'bg-blue-400 hover:bg-blue-500'
            }`}
          disabled={isProcessing}
          aria-label="Adım 1"
        />
        <div className={`w-6 h-0.5 ${productData ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`} />
        <button
          onClick={() => handleStepClick(WizardStep.PRODUCT_DETAIL)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentStep === WizardStep.PRODUCT_DETAIL
              ? 'bg-blue-600 transform scale-125'
              : isProcessing || !productData
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
                : currentStep > WizardStep.PRODUCT_DETAIL || productData
                  ? 'bg-blue-400 hover:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
            }`}
          disabled={!productData || isProcessing}
          aria-label="Adım 2"
        />
        <div className={`w-6 h-0.5 ${currentStep >= WizardStep.ISSUE_DETAILS ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`} />
        <button
          onClick={() => handleStepClick(WizardStep.ISSUE_DETAILS)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentStep === WizardStep.ISSUE_DETAILS
              ? 'bg-blue-600 transform scale-125'
              : isProcessing || !productData || !canProceedInReviseMode || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
                : productData && (currentStep > WizardStep.ISSUE_DETAILS || issueReason !== null)
                  ? 'bg-blue-400 hover:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
            }`}
          disabled={!productData || !canProceedInReviseMode || isProcessing || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))}
          aria-label="Adım 3"
        />
        <div className={`w-6 h-0.5 ${currentStep >= WizardStep.PART_SELECTION && canProceedInReviseMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`} />
        <button
          onClick={() => handleStepClick(WizardStep.PART_SELECTION)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentStep === WizardStep.PART_SELECTION
              ? 'bg-blue-600 transform scale-125'
              : isProcessing || !isIssueDetailsValid || !canProceedInReviseMode || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
                : isIssueDetailsValid && canProceedInReviseMode
                  ? 'bg-blue-400 hover:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
          }`}
          disabled={!isIssueDetailsValid || !canProceedInReviseMode || isProcessing || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))}
          aria-label="Adım 4"
        />
        <div className={`w-6 h-0.5 ${currentStep >= WizardStep.MEDIA_UPLOAD && canProceedInReviseMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        }`} />
        <button
          onClick={() => handleStepClick(WizardStep.MEDIA_UPLOAD)}
          className={`w-3 h-3 rounded-full transition-all duration-300 ${currentStep === WizardStep.MEDIA_UPLOAD
              ? 'bg-blue-600 transform scale-125'
              : isProcessing || !isIssueDetailsValid || !canProceedInReviseMode || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))
                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
                : isIssueDetailsValid && canProceedInReviseMode
                  ? 'bg-blue-400 hover:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50'
          }`}
          disabled={!isIssueDetailsValid || !canProceedInReviseMode || isProcessing || (productData && (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI))}
          aria-label="Adım 5"
        />
      </div>
    );
  };

  const renderSerialNumberStep = () => {
    return (
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2 text-center">Seri Numarası Kontrolü</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">Ürün bilgileri için seri numarasını girin</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Seri Numarası
            </label>
            <div className="relative">
              <input
                id="serialNumber"
                type="text"
                value={serialNumber}
                onChange={(e) => {
                  setSerialNumber(e.target.value);
                  // Seri numarası değiştiğinde hata mesajını temizle
                  if (error) {
                    setError('');
                  }
                }}
                className="form-input appearance-none block w-full pr-12 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500"
                placeholder="Seri numarasını girin"
                required
              />
              
              {/* Barkod okuyucu butonu */}
              <button
                type="button"
                onClick={() => setIsBarcodeScannerOpen(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                  <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                  <rect x="7" y="7" width="10" height="10"/>
                  <rect x="9" y="9" width="6" height="6"/>
                </svg>
              </button>


            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`
              w-full flex items-center justify-center px-6 py-3.5
              text-base font-semibold text-white
              bg-gradient-to-r from-blue-600 to-indigo-600
              hover:from-blue-700 hover:to-indigo-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              rounded-lg shadow-lg shadow-blue-500/25
              transform transition-all duration-200
              hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
              active:scale-[0.98]
              disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100
              disabled:hover:shadow-lg disabled:hover:shadow-blue-500/25
              relative overflow-hidden
              before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
            `}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kontrol Ediliyor...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                Kontrol Et
              </div>
            )}
          </button>
        </form>
      </div>
    );
  };

  const renderProductDetailsStep = () => {
    if (!productData) return null;

    return (
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
            </svg>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Ürün Bilgileri</h2>
          <button
            onClick={goBack}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Geri Dön
          </button>
        </div>

        <div className="border border-indigo-100 dark:border-gray-600 rounded-lg bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 mb-6">
          <div className="rounded-lg p-6 border border-white dark:border-gray-700 space-y-3">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Model</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.ModelAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Kategori</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.KategoriAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Renk</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.RenkAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Seri No</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.Sasi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Motor No</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.Motor || '-'}</p>
              </div>
          <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Satıcı Adı</h3>
            <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.SaticiAdi || productData.CariAdi || '-'}</p>
              </div>
              {productData.AliciTeslimEdilen && (
            <div className="pt-3 border-t border-indigo-200 dark:border-gray-600 space-y-3">
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Müşteri Adı</h3>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.AliciTeslimEdilen}</p>
                  </div>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Müşteri Telefonu</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {isValidTurkishMobile(productData.AliciTelefon) 
                    ? formatPhoneNumber(productData.AliciTelefon)
                    : productData.AliciTelefon
                  }
                </p>
                  </div>
                  {productData.AliciFaturaTarihi && (
                    <div className="mb-3">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Fatura Tarihi</h3>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {formatDate(productData.AliciFaturaTarihi)}
                      </p>
                    </div>
                  )}
                  {productData.AliciFaturaNo && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Fatura No.</h3>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {productData.AliciFaturaNo}
                      </p>
                    </div>
                  )}                  
                </div>
              )}
          </div>
        </div>

        {/* Garanti Durumu - Ayrı Bölüm */}
        <div className={`border rounded-lg mb-6 ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
            ? 'border-green-400 dark:border-green-400 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-green-900/20 dark:via-green-900/15 dark:to-green-800/20'
            : 'border-red-400 dark:border-red-400 bg-gradient-to-br from-red-50 via-rose-50 to-red-100 dark:from-red-900/20 dark:via-red-900/15 dark:to-red-800/20'
        }`}>
          <div className="rounded-lg p-4 border border-white dark:border-gray-700">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                  ? 'bg-green-500'
                  : 'bg-red-500'
              }`}>
                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
              </svg>
              </div>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                     ? 'text-green-800 dark:text-green-200'
                     : 'text-red-800 dark:text-red-200'
                 }`}>
                  Garanti Durumu
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                     ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700'
                     : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700'
                 }`}>
                   {(productData.SonIslem === ActionType.CANCELLED) ? 'İptal Edildi' : getWarrantyStatusText(productData.GarantiDurumu)}
                 </span>
              </div>
              
                             <div className="mt-2 space-y-1">
                 {productData.GarantiBitisTarihi && (
                  <p className={`text-sm ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                       ? 'text-green-700 dark:text-green-300'
                       : 'text-red-700 dark:text-red-300'
                   }`}>
                     <span className="font-medium">Bitiş Tarihi:</span> {formatDate(productData.GarantiBitisTarihi)}
                   </p>
                 )}
                 
                 {(productData.GarantiDurumuAciklamasi || productData.SonIslem === ActionType.CANCELLED) && (
                  <p className={`text-sm ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                       ? 'text-green-700 dark:text-green-300'
                       : 'text-red-700 dark:text-red-300'
                   }`}>
                     <span className="font-medium">Açıklama:</span> {productData.SonIslem === ActionType.CANCELLED ? 'Bu ürün için işlem iptal edilmiştir.' : productData.GarantiDurumuAciklamasi}
                   </p>
                 )}
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Alıcı Bilgilerini Gir düğmesi - Yeni oluşturmada sadece onay bekleyen veya oluşturulmuş, revizede iptal edilmiş de olabilir */}
          {(
            (productData.SonIslem === ActionType.CREATED || productData.SonIslem === ActionType.WAITING_FOR_APPROVAL) ||
            (
              productData.IslemAdi === IslemAdi.ONAYLANDI &&
              (productData.SonIslem === ActionType.CANCELLED || productData.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI)
            )
          ) && (
            <button
              type="button"
              onClick={() => {
                setIsBuyerInfoModalOpen(true);
              }}
              className="w-full text-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 active:text-indigo-900 dark:active:text-indigo-200 text-sm font-medium transition-all duration-200 mb-3 transform active:scale-95 focus:outline-none outline-none border-none ring-0 focus:ring-0 focus:border-none active:outline-none active:border-none"
              >
                {productData.SonIslem === ActionType.WAITING_FOR_APPROVAL ? 'Müşteri Bilgilerini Güncelle' : 'Müşteri Bilgilerini Gir'}
              </button>
          )}

          {/* Devam Et düğmesi */}
          <button
            type="button"
            onClick={(productData.GarantiDurumu === 1 && (productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI)) ? handleContinueToIssueDetails : undefined}
            disabled={productData.GarantiDurumu !== 1 || (productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI)}
            className={`
              w-full flex items-center justify-center px-6 py-3.5
              text-base font-semibold 
              ${(productData.GarantiDurumu === 1 && (productData.IslemAdi === IslemAdi.OLUSTURULDU || productData.IslemAdi === IslemAdi.ONAYLANDI))
                ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700'
                : 'text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
              }
              rounded-lg
            `}
          >
            <span className="hidden sm:inline">
              {(productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI)
                ? (productData.SonIslem === ActionType.CANCELLED ? 'İptal Edildi - Devam Edilemez' : 'Onay Bekleniyor - Devam Edilemez')
                : productData.GarantiDurumu === 1 
                  ? 'Devam Et' 
                  : 'Garanti Dışı - Devam Edilemez'
              }
            </span>
            <span className="sm:hidden">
              {(productData.IslemAdi !== IslemAdi.OLUSTURULDU && productData.IslemAdi !== IslemAdi.ONAYLANDI)
                ? (productData.SonIslem === ActionType.CANCELLED ? 'İptal Edildi' : 'Onay Bekleniyor')
                : productData.GarantiDurumu === 1 
                  ? 'Devam Et' 
                  : 'Garanti Dışı'
              }
            </span>
          </button>
        </div>
      </div>
    );
  };

  // Arıza nedeni ve kilometre girişi için yeni adım
  const renderIssueDetailsStep = () => {
    return (
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Arıza Detayları</h2>
          <button
            onClick={handleBackToProductDetails}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Geri Dön
          </button>
        </div>

        <form onSubmit={handleIssueDetailsSubmit} className="space-y-6">
          {/* Kilometre alanı - sadece KmIsRequired true ise göster */}
          {userData?.KmIsRequired && (
            <div>
              <label htmlFor="mileage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kilometre <span className="text-red-500">*</span>
              </label>
              <input
                id="mileage"
                type="number"
                value={mileage}
                onChange={handleMileageChange}
                min="0"
                className={mileageError ? 'form-input-error w-full appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]' : 'form-input w-full appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                placeholder="Kilometre bilgisini girin"
              />
            </div>
          )}

          {/* Arıza Nedeni Seçimi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arıza Nedeni <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div
                onClick={() => setIssueReasonModalOpen(true)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIssueReasonModalOpen(true);
                  }
                }}
                className={reasonError ? 'form-combobox-error w-full cursor-pointer' : 'form-combobox w-full cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500'}
              >
                <span className={issueReason ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                  {loadingIssueReasons ? 'Yükleniyor...' : (issueReason ? issueReason.Ad : 'Arıza nedeni seçin')}
                      </span>
                <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${issueReasonModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
                    </div>
            </div>
          </div>

          <div>
            <label htmlFor="serviceNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Servis Notları <span className="text-red-500">*</span>
            </label>
            <textarea
              id="serviceNotes"
              value={serviceNotes}
              onChange={handleServiceNotesChange}
              className={serviceNotesError ? 'form-textarea-error w-full appearance-none' : 'form-textarea w-full appearance-none bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
              placeholder="Servis notlarınızı buraya yazın"
              rows={4}
            />
          </div>

          <button
            type="submit"
            className={`
              w-full flex items-center justify-center px-6 py-3.5
              text-base font-semibold text-white
              bg-gradient-to-r from-blue-600 to-indigo-600
              hover:from-blue-700 hover:to-indigo-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              rounded-lg shadow-lg shadow-blue-500/25
              transform transition-all duration-200
              hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
              active:scale-[0.98]
              relative overflow-hidden
              before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
            `}
          >
            Devam Et
          </button>
        </form>
      </div>
    );
  };

  // Alıcı bilgileri adımı için render fonksiyonu


  // Parça seçimi adımı için render fonksiyonu - yeni dinamik sistem
  const renderPartSelectionStep = () => {
    return (
      <PartSelectionStep
        selectedSpareParts={selectedSpareParts}
        loadingPartGroups={loadingPartGroups}
        partGroups={partGroups}
        partGroupError={partGroupError}
        productData={productData}
        onBackToIssueDetails={handleBackToIssueDetails}
        onContinue={handlePartSelectionSubmit}
        onPartUpdate={setSelectedSpareParts}
        onShowMessage={showToast}
      />
    );
  };

  // Parça adımından arıza detayları adımına dönüş
  const handleBackToIssueDetails = () => {
    setDirection(-1);
    setCurrentStep(WizardStep.ISSUE_DETAILS);
    updateUrlParams(WizardStep.ISSUE_DETAILS);
  };

  // Parça grup verilerini API'den çek
  const fetchPartGroups = async () => {
    if (!productData) return;

    setLoadingPartGroups(true);
    setPartGroupError('');

    try {
      const data = await getPartGroups(productData.KategoriAdi);

      if (data.total === 0) {
        setPartGroupError('Bu kategori için parça grubu bulunamadı.');
        setPartGroups([]);
      } else {
        setPartGroups(data.data);
      }
    } catch (err) {
      setPartGroupError('Parça grupları yüklenirken bir hata oluştu.');
    } finally {
      setLoadingPartGroups(false);
    }
  };

  // Eski parça seçimi fonksiyonları kaldırıldı - artık yeni dinamik sistem kullanılıyor

  // Parça form gönderimi - yeni sistem
  const handlePartSelectionSubmit = () => {
    // Medya yükleme adımına geç
    setDirection(1);
    setCurrentStep(WizardStep.MEDIA_UPLOAD);
    updateUrlParams(WizardStep.MEDIA_UPLOAD);
  };

  // Medya adımından parça seçimi adımına dönüş
  const handleBackToPartSelection = () => {
    setDirection(-1);
    setCurrentStep(WizardStep.PART_SELECTION);
    updateUrlParams(WizardStep.PART_SELECTION);
  };

  const formatTalepDateTime = () => {
    const now = new Date();
    return `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:00`;
  };

  const formatUsedPartsMessage = (parts: UsedWarrantyPart[]) => {
    const visibleParts = parts.slice(0, 10);
    const lines = visibleParts.map(part => part.Kod ? `${part.Kod} - ${part.Ad}` : part.Ad);
    const noun = parts.length > 1 ? 'yedek parçalar' : 'yedek parça';

    return `Aşağıdaki ${noun} daha önce başka bir garanti işleminde kullanılmış:\n\n${lines.join('\n')}\n\nDevam etmek istiyor musunuz?`;
  };

  const handleCreateRequest = async () => {
    const partIds = selectedSpareParts
      .map(selectedPart => selectedPart.part.Id)
      .filter(id => id > 0);

    const uniquePartIds = Array.from(new Set(partIds));

    const shouldCheck =
      !skipWarrantyPartCheckRef.current &&
      userData?.ProductControlOnWarranty === true &&
      uniquePartIds.length > 0;

    if (!shouldCheck) {
      skipWarrantyPartCheckRef.current = false;
      setIsConfirmationOpen(true);
      return;
    }

    const kuponId = isReviseMode ? reviseData?.Kupon : productData?.Id;
    if (!kuponId) {
      setIsConfirmationOpen(true);
      return;
    }

    setWarrantyPartCheckPending(true);

    try {
      const usedParts = await checkUsedWarrantyParts({
        sparePartIds: uniquePartIds,
        kuponId,
        talepId: isReviseMode ? parseInt(reviseId || '0', 10) : 0,
        talepTarihi: formatTalepDateTime()
      });

      if (usedParts.length > 0) {
        setUsedPartsDialog({ isOpen: true, parts: usedParts });
        return;
      }

      setIsConfirmationOpen(true);
    } catch (error) {
      console.error('Yedek parça kontrolü hatası:', error);
      showToast('Yedek parça kontrolü sırasında bir hata oluştu.', 'error');
    } finally {
      setWarrantyPartCheckPending(false);
    }
  };

  const handleUsedPartsConfirm = () => {
    setUsedPartsDialog({ isOpen: false, parts: [] });
    skipWarrantyPartCheckRef.current = true;
    setIsConfirmationOpen(true);
  };

  const handleUsedPartsCancel = () => {
    setUsedPartsDialog({ isOpen: false, parts: [] });
  };

  // Medya yükleme adımı için render fonksiyonu
  const renderMediaUploadStep = () => {
    return (
      <MediaUploadStep
        mediaFiles={mediaFiles}
        uploadingFiles={uploadingFiles}
        isUploading={isUploading}
        mediaUploadError={mediaUploadError}
        onBackToPartSelection={handleBackToPartSelection}
        onMediaFilesUpdate={setMediaFiles}
        onMediaUploadErrorUpdate={setMediaUploadError}
        onCreateRequest={handleCreateRequest}
        isCheckingWarrantyParts={warrantyPartCheckPending}
      />
    );
  };

  // Yön tabanlı animasyon varyantları
  const variants = {
    enter: (direction: number) => {
      return {
        y: direction > 0 ? 100 : -40, // Geri gelirken sadece 40px yukarıdan gelmeli
        opacity: 0
      };
    },
    center: {
      y: 0,
      opacity: 1
    },
    exit: (direction: number) => {
      return {
        y: direction < 0 ? 100 : -40, // Kaybolurken sadece 40px yukarı gitmeli
        opacity: 0
      };
    }
  };

  // Arama terimine göre filtrelenmiş parça grupları
  const filteredPartGroups = partGroups.filter(
    group => group.Ad.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isPartGroupSheetOpen, setIsPartGroupSheetOpen] = useState(false);
  const selectedGroupRef = useRef<HTMLButtonElement | null>(null);

  // Menü açıldığında seçili gruba kaydır
  useEffect(() => {
    if (isPartGroupSheetOpen && selectedGroupRef.current) {
      selectedGroupRef.current.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  }, [isPartGroupSheetOpen]);

  // Seçili parça grubu state'i
  const [selectedPartGroup, setSelectedPartGroup] = useState<PartGroup | null>(null);

  // Parça grup seçim handler'ı - yeni sistemde PartGroupSelector açar
  // Bu handler artık PartSelectionStep içinde

  // Bu handler artık PartSelectionStep içinde yönetiliyor

  // SparePartInfoModal handler'ları






  // Bu handler artık PartSelectionStep içinde yönetiliyor



  return (
    <>
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    {isReviseMode ? 'Talep Revize Et' : 'Servis Talebi'}
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    {isReviseMode ? 'Mevcut talebinizi güncelleyin' : 'Yeni servis talebi oluşturun'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating Geri Butonu - Sadece mobilde görünür ve ilk adımda gizli */}
          <AnimatePresence>
            {!loading && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && currentStep !== WizardStep.SERIAL_CHECK && !uploadingFiles && !isUploading && !isRequestCompleted && (
              <motion.div
                className="fixed top-4 right-4 z-50 md:hidden"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.4
                }}
              >
                <motion.button
                  onClick={handleGoBack}
                  className="relative border border-gray-200 dark:border-gray-700 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 shadow-lg shadow-gray-500/25 dark:shadow-black/25 hover:shadow-xl hover:shadow-gray-500/30 dark:hover:shadow-black/35 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                  aria-label="Geri Git"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="rounded-full p-2.5 border border-white dark:border-gray-700 text-gray-700 dark:text-gray-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  </div>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={variants}
            initial={isInitialRender ? "center" : "enter"}
            animate="center"
            exit="exit"
            transition={{
              y: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden"
          >
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
                    {currentStep === WizardStep.SERIAL_CHECK && renderSerialNumberStep()}
        {currentStep === WizardStep.PRODUCT_DETAIL && renderProductDetailsStep()}
        {currentStep === WizardStep.ISSUE_DETAILS && renderIssueDetailsStep()}
        {currentStep === WizardStep.PART_SELECTION && renderPartSelectionStep()}
        {currentStep === WizardStep.MEDIA_UPLOAD && renderMediaUploadStep()}

            {renderStepIndicators()}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

        {/* Modal'lar artık PartSelectionStep içinde yönetiliyor */}

      {/* Servis Talebi Onay Diyaloğu */}
      <ConfirmationDialog
        isOpen={usedPartsDialog.isOpen}
        title="Onay"
        message={usedPartsDialog.parts.length > 0 ? formatUsedPartsMessage(usedPartsDialog.parts) : ''}
        confirmText="Evet"
        cancelText="Hayır"
        onCancel={handleUsedPartsCancel}
        onConfirm={handleUsedPartsConfirm}
      />

      <ConfirmationDialog
        isOpen={isConfirmationOpen}
        title="Servis Talebi Onayı"
        message="Servis talebiniz fabrika onayına gönderilecektir. Girdiğiniz bilgilerin doğruluğundan eminseniz Tamam düğmesine tıklayın."
        autoClose={true}
        onCancel={() => {
          skipWarrantyPartCheckRef.current = false;
          setIsConfirmationOpen(false);
        }}
        onConfirm={async () => {
          skipWarrantyPartCheckRef.current = false;
          setUploadingFiles(true);
          
          // Body'ye yükleme durumu attribute'u ekle
          document.body.setAttribute('data-uploading', 'true');
          
          // Eğer yüklenecek görsel veya video yoksa scroll'u atla
          const hasMedia = mediaFiles.length > 0;
          if (!hasMedia) {
            setSkipScroll(true);
          }
          
          try {
            // 1. Önce talep numarası al
            const requestNumber = await getRequestNumber();
            
              // 2. Servis talebi verilerini hazırla - yeni sistem artık selectedSpareParts kullanıyor
            
            // 3. Talep tipi belirleme: Alıcı talebi veya Satıcı talebi
            const requestType = productData?.AliciFaturaNo ? RequestType.BUYER : RequestType.SELLER; // Fatura numarası doluysa alıcı, boşsa satıcı talebi
            
            // 4. Tarih oluştur
            const formattedDate = formatTalepDateTime();
            
            // 5. Servis talebi verisini oluştur
            const requestData = {
              Kupon: isReviseMode ? reviseData?.Kupon : productData?.Id,
              No: requestNumber,
              Tip: requestType,
              Km: parseInt(mileage),
                ArizaNedeni: issueReason?.Id || 1, // Dinamik arıza nedeni ID'si
              ServisNotlari: serviceNotes, 
              Tarih: formattedDate,
              SonIslem: ActionType.WAITING_FOR_APPROVAL
            } as any;
            
              // 6. Servis talebini oluştur (yedek parçalar ayrı gönderilecek)
            const result = await createServiceRequest(requestData);
            
            if (result.success) {
              const talepId = result.data?.Id;
              
              // Talep ID'sini sakla
              if (talepId) {
                setCreatedRequestId(talepId);

                  // 7. Seçilen yedek parçaları ayrı ayrı gönder
                  try {
                    for (const selectedSparePart of selectedSpareParts) {
                      const sparePartData = {
                        Talep: talepId,
                        YedekParca: selectedSparePart.part.Id,
                        Miktar: selectedSparePart.quantity,
                        IscilikBedeli: 0,
                        Tracker: ""
                      };

                      const sparePartResult = await createServiceRequestSparePart(sparePartData);
                      if (sparePartResult.success) {
                      } else {
                      }
                    }
                  } catch (sparePartError) {
                    console.error('Yedek parçalar eklenirken hata:', sparePartError);
                    // Yedek parça hatası talep oluşturmayı engellemez
                  }
              }
              
              // 8. Tüm görsel dosyaları upload et (revize modunda mevcut görseller de yeni talep ile ilişkilendirilmeli)
              const allImageFiles = mediaFiles.filter(file => file.type === 'image');
              
              if (allImageFiles.length > 0 && talepId) {
                setIsUploading(true);
                
                // Her görsel için tek tek yükleme
                for (const mediaFile of allImageFiles) {
                  try {
                    // Upload durumunu 'uploading' yap
                    setMediaFiles(prev => prev.map(file => 
                      file.id === mediaFile.id 
                        ? { ...file, uploadStatus: 'uploading' } 
                        : file
                    ));
                    
                  // Mevcut dosyalar için gerçek File object'i oluştur
                  let fileToUpload = mediaFile.file;
                  if (mediaFile.existingFileId && mediaFile.existingFileRumuz) {
                    try {
                      // API'den görsel dosyasını download et - /download endpoint'ini kullan
                      const response = await fetch(`${API_BASE_URL}/files/download/${mediaFile.existingFileRumuz}`);
                      const blob = await response.blob();
                      // Blob'u File object'e çevir
                      fileToUpload = new File([blob], mediaFile.file.name, { type: blob.type || 'image/jpeg' });
                    } catch (downloadError) {
                      console.error('Mevcut dosya download hatası:', downloadError);
                      // Download başarısızsa fake file ile devam et
                    }
                  }
                    
                    // Dosyayı yükle
                    await FileService.uploadFile(
                      fileToUpload,
                      'Talepler', 
                      talepId,
                      true // thumb
                    );
                    
                    // Başarılı yükleme
                    setMediaFiles(prev => prev.map(file => 
                      file.id === mediaFile.id 
                        ? { ...file, uploadStatus: 'success' } 
                        : file
                    ));
                    
                  } catch (uploadError) {
                    console.error('Dosya yükleme hatası:', uploadError);
                    
                    // Hatalı yükleme
                    setMediaFiles(prev => prev.map(file => 
                      file.id === mediaFile.id 
                        ? { ...file, uploadStatus: 'error' } 
                        : file
                    ));
                  }
                }
                
                setIsUploading(false);
              }

              // 8.1. Video dosyasını upload et (sadece 1 video destekleniyor)
              const videoToUpload = mediaFiles.find(file => file.type === 'video');
              
              if (videoToUpload && talepId) {
                setIsUploading(true);
                try {
                  // Upload durumunu 'uploading' yap
                  setMediaFiles(prev => prev.map(file => 
                    file.id === videoToUpload.id 
                      ? { ...file, uploadStatus: 'uploading' } 
                      : file
                  ));
                  
                  // Mevcut dosyalar için gerçek File object'i oluştur
                  let fileToUpload = videoToUpload.file;
                  if (videoToUpload.existingFileId && videoToUpload.existingFileRumuz) {
                    try {
                      // API'den video dosyasını download et - /stream endpoint'ini kullan
                      const response = await fetch(`${API_BASE_URL}/files/stream/${videoToUpload.existingFileRumuz}`);
                      const blob = await response.blob();
                      // Blob'u File object'e çevir
                      fileToUpload = new File([blob], videoToUpload.file.name, { type: blob.type || 'video/mp4' });
                    } catch (downloadError) {
                      console.error('Mevcut video download hatası:', downloadError);
                      // Download başarısızsa fake file ile devam et
                    }
                  }
                  
                  // Dosyayı yükle
                  await FileService.uploadFile(
                    fileToUpload,
                    'Talepler', 
                    talepId,
                    true // thumb (backend şu an bu parametreyi kullanmıyor)
                  );
                  
                  // Başarılı yükleme
                  setMediaFiles(prev => prev.map(file => 
                    file.id === videoToUpload.id 
                      ? { ...file, uploadStatus: 'success' } 
                      : file
                  ));
                } catch (uploadError) {
                  console.error('Video yükleme hatası:', uploadError);
                  
                  // Hatalı yükleme
                  setMediaFiles(prev => prev.map(file => 
                    file.id === videoToUpload.id 
                      ? { ...file, uploadStatus: 'error' } 
                      : file
                  ));
                } finally {
                  setIsUploading(false);
                }
              }
              
              // 9. Başarı animasyonunu göster
              setIsSuccessVisible(true);
              setIsRequestCompleted(true); // Talep tamamlandı, geri tuşunu kalıcı olarak gizle
              
              // 10. React Query cache'ini güncelle
              queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
              
              // 11. Account cache'i güncelle
              if (!reviseId) { // Sadece yeni talep oluşturulduğunda, revize değilse
                notifyAccountCacheUpdate('request');
              }
            } else {
              console.error('Servis talebi oluşturma hatası:', result.msg || result.error);
              showToast(`Servis talebi oluşturulurken bir hata oluştu: ${result.msg || result.error || 'Bilinmeyen hata'}`, 'error');
            }
          } catch (error) {
            console.error('Servis talebi işlemi sırasında hata:', error);
            showToast('Servis talebi oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.', 'error');
          } finally {
            setUploadingFiles(false);
            // Body'den yükleme durumu attribute'unu kaldır
            document.body.removeAttribute('data-uploading');
          }
        }}
      />
      
      {/* Başarı Animasyonu */}
      <SuccessAnimation
        isVisible={isSuccessVisible}
        message="Servis talebiniz alınmıştır.<br />Servis detaylarınız yükleniyor..."
        duration={3000}
        onAnimationComplete={() => {
          // Önce başarı animasyonunu kapat
          setIsSuccessVisible(false);
          
          // Animasyon tamamen kapandıktan sonra kısa bir süre bekle
                      setTimeout(() => {
              // Servis detay sayfasına yönlendir
              if (createdRequestId) {
                navigate(`/service/${createdRequestId}`);
              } else {
                navigate('/'); // Fallback olarak ana sayfaya yönlendir
              }
            }, 500);
        }}
      />

        {/* Arıza Nedeni Seçim Modal */}
        <IssueReasonSelector
          isOpen={issueReasonModalOpen}
          onClose={() => setIssueReasonModalOpen(false)}
          onSelect={handleIssueReasonSelect}
          reasons={issueReasons}
          searchTerm={issueReasonSearchTerm}
          onSearchChange={handleIssueReasonSearchChange}
          selectedReasonId={issueReason?.Id}
      />
      
      {/* Alıcı Bilgileri Modal */}
      <BuyerInfoModal
        isOpen={isBuyerInfoModalOpen}
        onClose={() => setIsBuyerInfoModalOpen(false)}
        onSuccess={handleBuyerInfoSuccess}
        onError={handleBuyerInfoError}
        onAlreadyExists={handleBuyerInfoAlreadyExists}
        productId={String(productData?.Id || '')}
        serialNumber={productData?.Sasi || ''}
          productData={productData}
        initialBuyerInfo={productData ? {
          faturaNo: productData.AliciFaturaNo || '',
          belgeNo: productData.AliciBelgeNo || '',
          faturaTarihi: formatDateFromApi(productData.AliciFaturaTarihi) || '',
          teslimTarihi: formatDateFromApi(productData.AliciTeslimTarihi) || '',
          teslimYeri: productData.AliciTeslimYeri || '',
          telefon: productData.AliciTelefon || '',
          teslimEdilen: productData.AliciTeslimEdilen || '',
          email: productData.AliciEPosta || '',
          adres: productData.AliciAdres || ''
        } : undefined}
      />

      {/* Barkod Okuyucu Modal */}
      <BarcodeScanner
        isOpen={isBarcodeScannerOpen}
        onClose={() => setIsBarcodeScannerOpen(false)}
        onScan={handleBarcodeScanned}
        onError={handleBarcodeScanError}
        onAutoCheck={handleAutoCheck}
      />



      </PageTransition>

      {/* Toast Bildirimi - Page Transition dışında */}
      <Toast 
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </>
  );
} 